import {FormProvider, SubmitHandler, useForm} from 'react-hook-form';
import {useSearchParams} from 'react-router-dom';
import {APP_SCREENS, BASE_URL} from '@/const';
import LockIcon from '@/icons/LockIcon';
import ToastifyService, {TOAST_TYPES} from '@/services/ToastifyService';
import useHandleBackendErrors from '@/hooks/useHandleBackendErrors';
import useHandleGeneralErrors from '@/hooks/useHandleGeneralErrors';
import useResetPassword from '@/queries/auth/useResetPassword';
import useSignIn from '@/queries/auth/useSignIn';
import useCurrentUser from '@/queries/users/useCurrentUser';
import Button from '@/ui/Button';
import Input from '@/ui/Input';
import Overlay from '@/ui/Overlay';
import RelatedAdsLogoMedia from '@/ui/RelatedAdsLogoMedia';
import {AuthSteps, ILoginFormsProps} from '../../interface';
import StepTitle from '../StepTitle';
import styles from './styles.module.scss';

interface IFormValues {
    user_login: string;
    reset_key: string;
    new_password: string;
}

const ResetForm = ({setStep = () => {}}: ILoginFormsProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const {mutate, isPending, error} = useResetPassword();
    const {mutate: loginUser, isPending: loginPending, error: loginError, isSuccess} = useSignIn();

    const user_login = searchParams.get('login') || '';
    const reset_key = searchParams.get('key') || '';

    const {
        data,
        isLoading,
        error: userError
    } = useCurrentUser({
        loginParams: {user_login, reset_key},
        _fields: 'mustResetPassword'
    });

    const isReg = data?.mustResetPassword;

    const formMethods = useForm<IFormValues>({
        values: {user_login, reset_key, new_password: ''}
    });

    const {
        handleSubmit,
        register,
        setError,
        formState: {errors}
    } = formMethods;

    const onSubmit: SubmitHandler<IFormValues> = data => {
        mutate(
            {data},
            {
                onSuccess: () => {
                    setSearchParams({});
                    ToastifyService.toast(
                        `Password ${isReg ? 'created' : 'updated'} successfully`,
                        TOAST_TYPES.success
                    );

                    if (isReg) {
                        loginUser(
                            {data: {user_login, user_password: data.new_password, remember: true}},
                            {
                                onSuccess: () => window.open(BASE_URL + APP_SCREENS.onboarding.slug, '_self'),
                                onError: () => setStep(AuthSteps.login)
                            }
                        );
                    } else {
                        setStep(AuthSteps.login);
                    }
                }
            }
        );
    };

    useHandleGeneralErrors({error: userError || loginError});
    useHandleBackendErrors({error, setError});

    return (
        <FormProvider {...formMethods}>
            <Overlay visible={isLoading || loginPending || isSuccess} />
            <RelatedAdsLogoMedia />
            <form onSubmit={handleSubmit(onSubmit)} className={styles.form} noValidate>
                <StepTitle title={isReg ? 'Set a password' : 'Set a new password'} />
                <Input
                    error={errors?.new_password?.message}
                    Icon={LockIcon}
                    inputProps={{
                        type: 'password',
                        placeholder: 'Password',
                        ...register('new_password', {required: 'Password is required'})
                    }}
                />
                <div className={styles.bottom}>
                    <Button loading={isPending} btnStyles={[styles.submit]}>
                        {isReg ? 'Submit' : 'Update'}
                    </Button>
                    {isReg && <div className={styles.extra}>and continue to Partner Application Form</div>}
                </div>
            </form>
        </FormProvider>
    );
};

export default ResetForm;
